/** Functions for converting between retention bookmark data structures */

import find from 'lodash/find';
import uniqBy from 'lodash/uniqBy';

import {PropertyType, ResourceType} from 'common/report/constants';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {renameEvent} from 'common/report/util/events';
import {propertiesUtils} from 'common/report/util/properties';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {convertSegfilterToPropertyFilter, Property} from 'common/widgets/property-filter-menu/models/segfilter';
import {
  isCohortSegment,
  isEventSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {getGroupByProperties} from 'common/query-builder/query-block/segment-menu/util';
import {FiltersOperator, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {getFirstSegment} from 'common/query-builder/query-block/segment-menu/types';
import {getFilterByCohorts} from 'common/query-builder/query-block/filter-menu/util';
import {Segmentation} from 'common/types/reports/query';

/**
 * Transforms bookmark event to UI state events
 */
export function bookmarkEventToEvent(
  event: RetentionBookmarkParams['event'],
  eventFilters: RetentionBookmarkParams['filters'],
  filtersOperator: RetentionBookmarkParams['filters_op'],
  customEvents: CustomEventDefinition[],
): QueryEventEntry {
  const isCustomEvent = event?.startsWith(`$custom_event:`);

  const filters = (eventFilters || []).map((filter) => convertSegfilterToPropertyFilter(filter));

  let eventName;
  let customEventId;
  if (isCustomEvent) {
    customEventId = parseInt(event.split(`:`)[1], 10);
    const maybeCustomEvent = find(customEvents, [`id`, customEventId]);
    if (maybeCustomEvent) {
      eventName = maybeCustomEvent.name;
    } else {
      eventName = `Deleted Custom Event`;
    }
  } else if (event === `$mp_anything_event`) {
    eventName = `Any event`;
  } else if (event) {
    eventName = renameEvent(event);
  } else {
    eventName = `Select Event`;
  }

  return {
    filters,
    filtersOperator,
    event: {
      ...(isCustomEvent ? {id: customEventId} : {}),
      custom: isCustomEvent,
      label: eventName,
      value: event,
    },
    type: ResourceType.Event,
  };
}

export type EventAttributionItem = {
  label: string;
  value?: string | number;
  index: string;
};
/**
 * Transforms bookmark event to UI state events attribution
 */
export function bookmarkEventToEventAttribution(events, customEvents: CustomEventDefinition[]): EventAttributionItem[] {
  type Event = Pick<EventAttributionItem, 'label' | 'value'>;
  const items: Event[] = events
    .map((event) => bookmarkEventToEvent(event, [], FiltersOperator.And, customEvents))
    .map((x) => {
      const {event} = x;
      return {label: event.label, value: event.value};
    });
  const uniqueItems = uniqBy(items, `value`);
  const _stringMap = {
    1: `1st step`,
    2: `2nd step`,
  };
  return uniqueItems.map((x, index) => ({...x, index: _stringMap[index + 1]}));
}

/**
 * Transforms UI state filter to bookmark event and filter
 */
export function filterToBookmarkFilter(filters: PropertyFilter[]) {
  const filterByEvent = filters.length
    ? ({
        operator: `and`,
        children: filters
          .filter((filter) => filter.resourceType === ResourceType.User || filter.resourceType === ResourceType.Event)
          .map((filter) => ({[filter.resourceType]: filter as Segmentation})),
      } as RetentionBookmarkParams['filter_by_event'])
    : {};

  const cohortFilters = filters.filter((filter) => filter.resourceType === ResourceType.Cohort);
  const filterByCohort = cohortFilters.length ? getFilterByCohorts(cohortFilters) : {};

  return {filterByEvent, filterByCohort};
}

/**
 * Transforms UI state event to bookmark event and filter
 */
export function eventToBookmarkEvent(ev: QueryEventEntry) {
  const {event, filters, filtersOperator} = ev;
  const segFilters = filters.map((filter) => (filter instanceof PropertyFilter ? filter.getAttributes() : filter));
  return {event: event.value, filters: segFilters, filtersOp: filtersOperator};
}

/**
 * Transforms a single group by dimension to old seg filter format.
 */
function groupByToOn(group): {on: Property} {
  if (Object.prototype.hasOwnProperty.call(group, `dataGroupId`)) {
    return {
      on: {
        name: group[`propertyName`],
        source: group[`resourceType`],
        data_group_id: group[`dataGroupId`],
        type: group[`propertyType`],
      },
    };
  } else {
    return {on: {name: group[`propertyName`], source: `user`, type: group[`propertyType`]}};
  }
}

type CohortsOrOn = {
  on?: Property;
  cohorts?: Array<{id: number; name: string}>;
};

/**
 * Transforms new UI state into old cohorts, on params, only one
 * of which can be set.
 */
export function groupByToCohortsOrOn(groupBy): CohortsOrOn {
  const group = groupBy ? groupBy[0] : null;
  if (Array.isArray(group) && isCohortSegment(group)) {
    return {
      cohorts: groupBy[0].map((c) => ({
        id: c[`cohort`][`id`],
        name: c[`cohort`][`name`],
      })),
    };
  } else if (group) {
    if (Object.prototype.hasOwnProperty.call(group, `user`)) {
      return groupByToOn(group[`user`]);
    } else if (Object.prototype.hasOwnProperty.call(group, `event`)) {
      return groupByToOn(group[`event`]);
    } else {
      // parse data group property
      const property = group[Object.keys(group)[0]];
      if (!property[`resourceType`] || !property[`propertyName`]) {
        return {};
      }
      return {
        on: {
          data_group_id: property[`resourceType`].substring(3),
          name: property[`propertyName`],
          source: `user`,
          type: PropertyType.String, //default property type when unknown
        },
      };
    }
  }
  return {};
}

/**
 * Transforms old UI state on into new group by.
 */
export function onToGroupBy(on): Segment[] | null {
  if (on) {
    const {source, name: propertyName} = on;
    // rename properties to event because we changed at some point
    const resourceType: ResourceType = (source === `properties` || source === `event`
      ? `event`
      : `user`) as ResourceType;
    const segmentation = {
      resourceType,
      propertyName,
      propertyType: PropertyType.String,
      propertyDefaultType: PropertyType.String,
    };

    if (source === `event`) {
      return [{event: segmentation}];
    } else {
      return [{user: segmentation}];
    }
  } else {
    return null;
  }
}

/**
 * Transforms old UI state to filtery_by_cohort and group_by
 */
export function cohortsToFilterByCohortAndGroupBy(cohorts, includeNegatedCohorts) {
  // 1 cohort is filter by, > 1 is group by
  if (cohorts.length === 1) {
    return {
      filter_by_cohort: {
        operator: `or`,
        children: cohorts.map(function (c) {
          return {cohort: {id: c.id, negated: false, name: c.name}};
        }),
      },
      group_by: null,
    };
  } else {
    if (includeNegatedCohorts) {
      return {
        group_by: [cohorts.flatMap((c) => [{cohort: {id: c.id, negated: true}}, {cohort: {id: c.id, negated: false}}])],
        filter_by_cohort: null,
      };
    } else {
      return {
        group_by: [cohorts.map((c) => ({cohort: {id: c.id, negated: false}}))],
        filter_by_cohort: null,
      };
    }
  }
}

export function convertOnStringToSegment(on: string): Property {
  // old-style, serialized arb selector
  const match = /(properties|user)\["(.+)"\]/.exec(on);
  if (match && match.length > 2) {
    return {
      name: match[2],
      source: match[1] === `properties` ? `event` : `user`,
      type: undefined,
    };
  }

  return null;
}

export function isSegmented(segmentGroups: Segment[]) {
  return segmentGroups?.length > 0;
}

export function getSegmentName(segmentGroups: Segment[]) {
  if (isSegmented(segmentGroups) && !isSegmentedByCohort(segmentGroups)) {
    return segmentGroups.map((group) => getGroupByName(group)).join(` / `);
    // TODO: revisit for B2B properties
  } else {
    return ``;
  }
}

export function getFlattenedSegmentType(segmentGroups: Segment[]) {
  if (isSegmented(segmentGroups) && !isSegmentedByCohort(segmentGroups)) {
    const groupByProperties = getGroupByProperties(segmentGroups);
    const segmentTypes = groupByProperties.flat().map((groupByProperty) => groupByProperty.propertyType);
    return segmentTypes.length === 1 ? segmentTypes[0] : PropertyType.String;
  } else {
    return PropertyType.String;
  }
}

export function isSegmentedByCohort(segmentGroups: Segment[]): boolean {
  return isSegmented(segmentGroups) && segmentGroups.some(isCohortSegment);
}

export function getGroupByName(group: Segment): string {
  if (isCohortSegment(group)) {
    return `Cohort`;
  } else if (isUserSegment(group) || isEventSegment(group)) {
    const firstSegment = getFirstSegment(group);
    const {propertyName, dataGroupId, customProperty, propertyObjectKey, listItemGroup} = firstSegment;
    return propertiesUtils.renameProperty(propertyName, isUserSegment(group) ? ResourceType.User : ResourceType.Event, {
      dataGroupId,
      customProperty,
      propertyObjectKey,
      listItemGroup,
    });
  } else {
    return `Cohort`;
  }
}

export function getDataGroupName(dataGroupId, plural = false): string {
  if (!dataGroupId) {
    return plural ? `users` : `user`;
  }

  const dataGroup = dataGroupStore.value[dataGroupId];
  if (dataGroup) {
    return `${dataGroup.displayName}${plural ? `(s)` : ``}`;
  } else {
    return `profile${plural ? `(s)` : ``}`;
  }
}
